<template>
  <v-app id="inspire">
    <navigation />

    <v-content id="content-wrapper">
      <v-container fluid>
        <nuxt />
      </v-container>

      <modal />
    </v-content>
  </v-app>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'

  // Utils
  import { createLogoutRoute, REFERRER_QUERY_PARAM } from '@/utils/routing'

  // Constants
  import { BASE_ACCOUNT_ROLES, ROLES } from '@/store/admin'

  // Components
  import Navigation from '@/components/layout/Navigation'
  import Modal from '@/components/layout/Modal'

  // Events
  import { EVENT_PAYMENT_ORDER_CREATE, EVENT_PAYMENT_ORDER_UPDATE } from '@/events/paymentOrders'
  import { EVENT_PAYMENT_CREATE, EVENT_PAYMENT_UPDATE } from '@/events/payments'
  import { EVENT_CARD_ORDER_CREATE, EVENT_CARD_ORDER_UPDATE } from '@/events/cardOrders'
  import { EVENT_SWIFT_MESSAGE_ORDER_CREATE, EVENT_SWIFT_MESSAGE_ORDER_UPDATE } from '@/events/swiftMessageOrders'

  const COUNTER_POLLING_INTERVAL = 20000

  const allowedClientRequestRoles = [
    ROLES.ACCOUNTANT_READ,
    ROLES.ACCOUNTANT_WRITE,
    ROLES.SUPPORT_READ,
    ROLES.SUPPORT_WRITE,
    ROLES.MANAGER,
    ROLES.MANAGER_LEAD
  ]

  const allowedSwiftMessageRoles = [
    ROLES.ACCOUNTANT_READ,
    ROLES.ACCOUNTANT_WRITE,
    ROLES.ACCOUNTANT_SWIFT
  ]

  export default {
    middleware: [
      'admin',
      'features',
      'employees',
      'currencies',
      'integrators',
      'licenses',
      'countries',
      'swiftMessageTypes',
      'swiftMessageOrderTypes'
    ],
    components: {
      Navigation,
      Modal
    },
    provide () {
      return {
        endCurrentUserSession: this.endCurrentUserSession
      }
    },
    data () {
      return {
        paymentCountersPollingTimer: null,
        paymentOrderTotalCountPollingTimer: null,
        swiftMessageOrderTotalCountPollingTimer: null,
        unreadClientRequestsPollingTimer: null,
        unreadForexRequestsPollingTimer: null,
        cardOrderTotalCountPollingTimer: null
      }
    },
    computed: {
      ...mapGetters('admin', ['admin', 'hasAuthorityReadPermission', 'hasRole', 'sessionTimeout', 'sessionTimeLeft'])
    },
    watch: {
      sessionTimeLeft (sessionTimeLeft) {
        if (sessionTimeLeft <= 0) {
          this.endCurrentUserSession()
        }
      },
      sessionTimeout (sessionTimeout) {
        sessionTimeout
          ? this.showModal({
            name: 'SessionTimeoutModal',
            canCloseOutside: false
          })
          : this.closeModal()
      }
    },
    created () {
      this.$eventHub.$on([
        EVENT_PAYMENT_CREATE,
        EVENT_PAYMENT_UPDATE
      ], this.loadPaymentCounters)

      this.$eventHub.$on([
        EVENT_PAYMENT_ORDER_CREATE,
        EVENT_PAYMENT_ORDER_UPDATE
      ], this.loadPaymentOrderTotalCount)

      this.$eventHub.$on([
        EVENT_SWIFT_MESSAGE_ORDER_CREATE,
        EVENT_SWIFT_MESSAGE_ORDER_UPDATE
      ], this.loadSwiftMessageOrderTotalCount)

      this.$eventHub.$on([
        EVENT_CARD_ORDER_CREATE,
        EVENT_CARD_ORDER_UPDATE
      ], this.loadCardOrderTotalCount)

      this.startSessionTimer({
        sessionCheckEveryMs: parseInt(this.$route.query.sessionCheckEveryMs),
        sessionModalThresholdMs: parseInt(this.$route.query.sessionModalThresholdMs),
        sessionModalMaxMs: parseInt(this.$route.query.sessionModalMaxMs)
      })

      this.startInitialPollings()

      this.registerVisibilityChangeHandler()
    },
    beforeDestroy () {
      this.$eventHub.$off([
        EVENT_PAYMENT_CREATE,
        EVENT_PAYMENT_UPDATE
      ], this.loadPaymentCounters)

      this.$eventHub.$off([
        EVENT_PAYMENT_ORDER_CREATE,
        EVENT_PAYMENT_ORDER_UPDATE
      ], this.loadPaymentOrderTotalCount)

      this.$eventHub.$off([
        EVENT_SWIFT_MESSAGE_ORDER_CREATE,
        EVENT_SWIFT_MESSAGE_ORDER_UPDATE
      ], this.loadSwiftMessageOrderTotalCount)

      this.$eventHub.$off([
        EVENT_CARD_ORDER_CREATE,
        EVENT_CARD_ORDER_UPDATE
      ], this.loadCardOrderTotalCount)

      this.stopSessionTimer()
      this.stopInitialPollings()

      this.unregisterVisibilityChangeHandler()
    },
    methods: {
      ...mapActions('admin', ['startSessionTimer', 'stopSessionTimer']),
      ...mapActions('payments', ['loadPaymentCounters', 'cancelLoadPaymentCounters']),
      ...mapActions('paymentOrders', ['loadPaymentOrderTotalCount', 'cancelLoadPaymentOrderTotalCount']),
      ...mapActions('swiftMessageOrders', ['loadSwiftMessageOrderTotalCount', 'cancelLoadSwiftMessageOrderTotalCount']),
      ...mapActions('clientRequests', [
        'loadUnreadClientRequestCount',
        'cancelLoadUnreadClientRequests',
        'cancelLoadForexUnreadClientRequests',
        'loadUnreadForexClientRequestCount'
      ]),
      ...mapActions('cardOrders', ['loadCardOrderTotalCount', 'cancelLoadCardOrderTotalCount']),
      ...mapActions('modal', ['showModal', 'closeModal']),
      onDocumentVisibilityChange () {
        if (document.hidden) {
          this.stopInitialPollings()
        } else {
          this.startInitialPollings()
        }
      },
      startInitialPollings () {
        if (this.hasRole(BASE_ACCOUNT_ROLES)) {
          this.startPaymentCountersPolling()
          this.startPaymentOrderTotalCountPolling()
          this.startCardOrderTotalCountPolling()
        }

        if (this.hasRole(allowedSwiftMessageRoles)) {
          this.startSwiftMessageOrderTotalCountPolling()
        }

        if (this.hasRole(allowedClientRequestRoles)) {
          this.startUnreadClientRequestsPolling()
        }

        if (this.hasRole(ROLES.ACCOUNTANT_WRITE)) {
          this.startUnreadForexRequestsPolling()
        }
      },
      stopInitialPollings () {
        this.stopPaymentCountersPolling()
        this.stopPaymentOrderTotalCountPolling()
        this.stopSwiftMessageOrderTotalCountPolling()
        this.stopCardOrderTotalCountPolling()
        this.stopUnreadClientRequestsPolling()
        this.stopUnreadForexRequestsPolling()
      },
      startPaymentCountersPolling () {
        clearTimeout(this.paymentCountersPollingTimer)

        this.loadPaymentCounters()
          .then(() => this.setPaymentCountersPollingTimer(
            setTimeout(this.startPaymentCountersPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      startPaymentOrderTotalCountPolling () {
        clearTimeout(this.paymentOrderTotalCountPollingTimer)

        this.loadPaymentOrderTotalCount()
          .then(() => this.setPaymentOrderTotalCountPollingTimer(
            setTimeout(this.startPaymentOrderTotalCountPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      startSwiftMessageOrderTotalCountPolling () {
        clearTimeout(this.swiftMessageOrderTotalCountPollingTimer)

        this.loadSwiftMessageOrderTotalCount()
          .then(() => this.setSwiftMessageOrderTotalCountPollingTimer(
            setTimeout(this.startSwiftMessageOrderTotalCountPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      startUnreadClientRequestsPolling () {
        clearTimeout(this.unreadClientRequestsPollingTimer)

        this.loadUnreadClientRequestCount()
          .then(() => this.setUnreadClientRequestsPollingTimer(
            setTimeout(this.startUnreadClientRequestsPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      startUnreadForexRequestsPolling () {
        clearTimeout(this.unreadForexRequestsPollingTimer)

        this.loadUnreadForexClientRequestCount()
          .then(() => this.setUnreadForexRequestsPollingTimer(
            setTimeout(this.startUnreadForexRequestsPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      startCardOrderTotalCountPolling () {
        clearTimeout(this.cardOrderTotalCountPollingTimer)

        this.loadCardOrderTotalCount()
          .then(() => this.setCardOrderTotalCountPollingTimer(
            setTimeout(this.startCardOrderTotalCountPolling, COUNTER_POLLING_INTERVAL)
          ))
      },
      stopPaymentCountersPolling () {
        clearTimeout(this.paymentCountersPollingTimer)
        this.cancelLoadPaymentCounters()
      },
      stopCardOrderTotalCountPolling () {
        clearTimeout(this.cardOrderTotalCountPollingTimer)
        this.cancelLoadCardOrderTotalCount()
      },
      stopPaymentOrderTotalCountPolling () {
        clearTimeout(this.paymentOrderTotalCountPollingTimer)
        this.cancelLoadPaymentOrderTotalCount()
      },
      stopSwiftMessageOrderTotalCountPolling () {
        clearTimeout(this.swiftMessageOrderTotalCountPollingTimer)
        this.cancelLoadSwiftMessageOrderTotalCount()
      },
      stopUnreadClientRequestsPolling () {
        clearTimeout(this.unreadClientRequestsPollingTimer)
        this.cancelLoadUnreadClientRequests()
      },
      stopUnreadForexRequestsPolling () {
        clearTimeout(this.unreadForexRequestsPollingTimer)
        this.cancelLoadForexUnreadClientRequests()
      },
      endCurrentUserSession () {
        this.$router.replace(createLogoutRoute({
          query: {
            [REFERRER_QUERY_PARAM]: window.location.href
          }
        }))
      },
      registerVisibilityChangeHandler () {
        document.addEventListener('visibilitychange', this.onDocumentVisibilityChange, {
          passive: true
        })
      },
      unregisterVisibilityChangeHandler () {
        document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange, {
          passive: true
        })
      },
      setPaymentCountersPollingTimer (timer) {
        this.$set(this, 'paymentCountersPollingTimer', timer)
      },
      setPaymentOrderTotalCountPollingTimer (timer) {
        this.$set(this, 'paymentOrderTotalCountPollingTimer', timer)
      },
      setSwiftMessageOrderTotalCountPollingTimer (timer) {
        this.$set(this, 'swiftMessageOrderTotalCountPollingTimer', timer)
      },
      setUnreadClientRequestsPollingTimer (timer) {
        this.$set(this, 'unreadClientRequestsPollingTimer', timer)
      },
      setUnreadForexRequestsPollingTimer (timer) {
        this.$set(this, 'unreadForexRequestsPollingTimer', timer)
      },
      setCardOrderTotalCountPollingTimer (timer) {
        this.$set(this, 'cardOrderTotalCountPollingTimer', timer)
      }
    }
  }
</script>

<style lang="scss">
  html {
    overflow-y: hidden;
  }

  html, body, #__nuxt, #__layout,
  .application, .v-content {
    height: 100%;
  }

  .v-content__wrap {
    overflow-y: auto;
  }

  @media only screen and (min-width: 960px) {
    .container {
      padding: 16px;
    }
  }

  .container.gutter-30 {
    padding-right: 30px;
    padding-left: 30px;
  }

  /* Rows */
  .container.grid-list-lg .row {
    &--data {
      word-break: break-word;

      > .flex {
        padding-top: 9px;
        padding-bottom: 9px;
      }
    }
  }

  /* Text */
  .text--highlight {
    background-color: #e3f2fd;
  }

  /* Chips */
  .v-chip--x-small {
    height: 21px;

    & .v-chip__content {
      padding: 0 8px;
    }
  }

  /* Pagination */
  .v-pagination {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  .v-pagination__item:focus {
    outline: none;
  }

  .pagination--limited {
    li:nth-last-child(2) > button::after {
      content: "+"
    }
  }

  /* Tables */
  .td--passive {
    opacity: .6;
  }

  table.v-table thead th {
    font-size: 13px;
    font-weight: 400;
  }

  table.v-table thead tr {
    height: 47px;
  }

  table.v-table tbody td,
  table.v-table tbody th {
    height: 50px;
  }

  .theme--light .v-table thead th,
  .application .theme--light.v-table thead th {
    color: #757575;
  }

  .theme--light .v-table,
  .application .theme--light.v-table {
    color: #323a45;
  }

  /** Rows per page control **/
  .v-datatable__actions {
    justify-content: space-between;
  }

  .v-datatable__actions .v-btn {
    display: none;
  }

  .v-datatable__actions__range-controls {
    order: 1;
  }

  .v-datatable__actions__pagination {
    margin-left: 30px;
  }

  .v-datatable__actions__select {
    order: 2;
    margin-right: 30px;
  }

  .v-datatable__actions__select .v-select {
    margin-top: 12px;
    margin-bottom: 12px;
  }

  /* Checkboxes */
  .v-input--checkbox {
    margin-top: 0;

    &.v-input--is-label-active label {
      color: #212121;
    }
  }

  /* Radio buttons */
  .v-input--radio-group--row {
    .v-input--radio-group__input {
      > .v-label {
        flex: 1 100%;
      }
    }
  }

  .v-input--radio-group__input {
    > .v-label {
      color: #212121;
      font-size: 14px;
      padding-bottom: 13px;
    }
  }

  .v-radio {
    &.primary--text label {
      color: #212121;
    }

    + .expandable-item {
      margin: 0 0 6px 33px;
    }
  }

  .v-input--radio-group:not(.v-input--hide-details) {
    .v-input__slot {
      margin-bottom: 8px;
    }
  }

  /* Inputs */
  .v-input {
    &--accent {
      input {
        font-weight: 500;
      }
    }
  }

  /* Select */
  .v-select > .v-input__control > .v-input__slot {
    cursor: pointer;
  }

  /* Autocomplete */
  .v-autocomplete__content {
    .v-list {
      padding-top: 15px;
      padding-bottom: 10px;

      &__tile {
        height: 55px;
      }
    }
  }

  /* Autocomplete menu */
  .v-menu__content {
    margin-top: 1px; // Fix autocomplete dropdown menu overlapping with input
  }

  /* Buttons */
  .v-btn {
    &.primary--text,
    &.v-btn--flat {
      font-weight: 700;
    }
  }

  /* Tabs */
  .v-tabs .v-tabs__div {
    font-weight: 600;
  }

  .v-tabs .v-tabs__item {
    padding-right: 19px;
    padding-left: 19px;
  }

  .theme--light .v-tabs__bar .v-tabs__div,
  .application .theme--light.v-tabs__bar .v-tabs__div {
    color: #1868a9;
  }

  .v-tabs__container {
    padding-right: 24px;

    .v-btn {
      margin-right: -6px;
    }
  }

  .v-tabs__items .container {
    padding: 16px 24px 16px 30px;
  }

  .v-tabs__items {
    overflow: unset;
  }

  /* Lists */
  .v-list__tile {
    user-select: auto;
  }

  .v-list__tile__avatar {
    user-select: none;
  }

  .theme--light.v-list
  .v-list__tile__mask {
    color: inherit;
    background: #e3f2fd;
  }

  /* Subheader */
  .v-subheader {
    &--dense {
      color: rgba(33, 33, 33, .54);
      font-size: 16px;
      font-weight: 400;
      height: auto;
      padding-left: 0;
    }
  }

  /* Utilities */
  .nowrap {
    white-space: nowrap;
  }

  .line-through {
    text-decoration: line-through;
  }
</style>
